import PropTypes from 'prop-types';
import styled from 'helpers/styles';
import {css} from 'styled-components';
import ComponentStyles from 'styles/mixins/Component.styles';
import Popper from '@mui/material/Popper';
import utils from 'helpers/utils';

const StyledContextPopper = styled(Popper)
  .attrs((props) => {
    let attr = props;

    const vars = {
      '--context-popper-min-width': props.theme.layout(props.$minWidth),
      '--context-popper-max-width': props.theme.layout(props.$maxWidth),
      '--context-popper-width': props.theme.layout(props.$width),
      '--context-popper-offset': props.$offset?.length > 0 ? `${props.$offset[1]}px` : null,
      '--context-popper-shadow': props.theme.property(`shadows.${props.$elevation}`),
      '--context-popper-inverted-shadow': props.theme.invertShadow(props.$elevation)
    };

    return utils.styleProps(attr, vars);
  })`
  display: flex;
  z-index: ${props => props.theme.property('zIndex.popper')};
  pointer-events: none;
  overscroll-behavior: contain;
  
  > .Paper {
    display: flex;
    flex-direction: column;
    pointer-events: all;
    box-shadow: unset;
    overflow: hidden;
    
    > .Wrapper {
      height: 100%;
      width: 100%;
      max-height: inherit;
      max-width: inherit;
      overflow: hidden;
    }
    
    ${props => utils.isDefined(props.$minWidth) ? css`
      width: ${props.$css.$var('--context-popper-min-width', false)} !important;
      min-width: ${props.$css.$var('--context-popper-min-width', false)} !important;
    ` : css``}

    ${props => utils.isDefined(props.$maxWidth) ? css`
      max-width: ${props.$css.$var('--context-popper-max-width', false)} !important;
    ` : css``}

    ${props => utils.isDefined(props.$width) ? css`
      width: ${props.$css.$var('--context-popper-width', false)} !important;
    ` : css``}
  }

  &[data-popper-placement="bottom"], &[data-popper-placement="top"] {
    justify-content: center;
  }

  &.ContextPopper-shadows {
    > .Paper {
      box-shadow: ${props => props.$css.$var('--context-popper-shadow', false)};
    }

    &.ContextPopper-reverseShadows {
      &[data-popper-placement="bottom"], &[data-popper-placement="bottom-start"], &[data-popper-placement="bottom-end"] {
        > .Paper {
          box-shadow: ${props => props.$css.$var('--context-popper-shadow', false)};
        }
      }

      &[data-popper-placement="top"], &[data-popper-placement="top-start"], &[data-popper-placement="top-end"] {
        > .Paper {
          box-shadow: ${props => props.$css.$var('--context-popper-inverted-shadow', false)};
        }
      }
    }
  }

  &.ContextPopper {
    > .Paper {
      outline: none;
      padding: ${props => props.theme.spacing(2)};
    }
  }

  &.ContextPopper-outlined {
    > .Paper {
      border: 1px solid ${props => props.theme.property('palette.divider')};
    }
  }
  
  &.ContextPopper-density-dense {
    > .Paper {
      padding: ${props => props.theme.spacing(1)};
    }
  }
  &.ContextPopper-density-denser {
    > .Paper {
      padding: ${props => props.theme.spacing(0.5)};
    }
  }
  &.ContextPopper-density-densest {
    > .Paper {
      padding: 0;
    }
  }

  &.ContextPopper-size-large {
    > .Paper {
      max-width: ${props => props.theme.layout(400)};
      max-height: min(${props => props.theme.layout(480)}, 52vh);
    }
  }
  &.ContextPopper-size-medium {
    > .Paper {
      max-width: ${props => props.theme.layout(320)};
      max-height: min(${props => props.theme.layout(300)}, 46vh);
    }
  }
  &.ContextPopper-size-small {
    > .Paper {
      max-width: ${props => props.theme.layout(240)};
      max-height: min(${props => props.theme.layout(260)}, 46vh);
    }
  }

  &.ContextPopper-stretch {
    > .Paper {
      width: 100%;
      max-width: unset;
    }
  }

  &.ContextPopper-transparent {
    > .Paper {
      background-color: transparent !important;
    }
  }

  &.ContextPopper-fixed {
    position: fixed !important;
    
    ${props => props.$offset?.length > 0 ? css`
      transform: translate3d(${props.$css.$var('--context-popper-offset', false)}, ${props.$css.$var('--context-popper-offset', false)}, 0) !important;
    ` : css``}
  }

  &.ContextPopper-radius-round {
    > .Paper {
      border-radius: ${props => props.theme.radius(2)};
    }
  }
  &.ContextPopper-radius-rounder {
    > .Paper {
      border-radius: ${props => props.theme.radius(3)};
    }
  }
  &.ContextPopper-radius-roundest {
    > .Paper {
      border-radius: ${props => props.theme.radius(4)};
    }
  }
  &.ContextPopper-radius-square {
    > .Paper {
      border-radius: ${props => props.theme.radius(0)};
    }
  }
  
  ${ComponentStyles}
`;

StyledContextPopper.propTypes = {
  theme: PropTypes.object
};

export default StyledContextPopper;
